import { useState } from "react";
import { Document, pdfjs } from "react-pdf";
import { useSignature, FIELD_TYPES } from "./SignatureContext";
import PdfRead from "./PdfRead";
import PdfWrite from "./PdfWrite";
import SignatureModal from "./SignatureModal";
import { PenTool, Calendar, Edit3, Check } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

if (typeof Promise !== "undefined" && !("withResolvers" in Promise)) {
  Promise.withResolvers = function () {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

export default function PdfViewer({ currentStep }) {
  const { pdfFile, activeFieldType, setActiveFieldType } = useSignature();

  const [numPages, setNumPages] = useState(0);
  const [activeBlockId, setActiveBlockId] = useState(null);

  if (!pdfFile) return null;

  const fieldTypes = [
    {
      type: FIELD_TYPES.SIGNATURE,
      label: "Signature",
      icon: <PenTool size={14} />,
      activeClass: "bg-emerald-500 text-white shadow-md",
      inactiveClass: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    },
    {
      type: FIELD_TYPES.INITIAL,
      label: "Initials",
      icon: <Edit3 size={14} />,
      activeClass: "bg-purple-500 text-white shadow-md",
      inactiveClass: "bg-purple-50 text-purple-700 hover:bg-purple-100",
    },
    {
      type: FIELD_TYPES.DATE,
      label: "Date",
      icon: <Calendar size={14} />,
      activeClass: "bg-amber-500 text-white shadow-md",
      inactiveClass: "bg-amber-50 text-amber-700 hover:bg-amber-100",
    },
  ];

  return (
    <div className="relative flex w-full max-h-[720px] flex-col items-center gap-3 overflow-auto rounded-lg bg-slate-100/70 p-3">
      {currentStep === 2 && (
        <div className="sticky top-0 z-20 flex items-center justify-center gap-3 rounded-lg bg-white/95 px-4 py-2 shadow-sm backdrop-blur">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-full border border-slate-200">
            {fieldTypes.map(
              ({ type, label, icon, activeClass, inactiveClass }) => {
                const isActive = activeFieldType === type;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveFieldType(type)}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                      ${
                        isActive
                          ? `${activeClass} shadow-sm`
                          : `${inactiveClass} hover:bg-slate-200/50`
                      }
                    `}
                  >
                    {isActive ? (
                      <Check
                        size={14}
                        strokeWidth={3}
                        className="border border-current rounded-sm"
                      />
                    ) : (
                      icon
                    )}
                    {label}
                  </button>
                );
              },
            )}
          </div>
        </div>
      )}
      <div className="overflow-auto rounded-lg bg-slate-100/70 p-3">
        <Document
          file={pdfFile}
          onLoadSuccess={(doc) => setNumPages(doc.numPages)}
          className="flex flex-col gap-6"
          loading={
            <div className="flex items-center justify-center p-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
            </div>
          }
        >
          {Array.from({ length: numPages }).map((_, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-md bg-white shadow-md"
            >
              {currentStep === 2 && <PdfRead pageNumber={index + 1} />}
              {currentStep === 4 && (
                <PdfWrite
                  pageNumber={index + 1}
                  onOpenModal={setActiveBlockId}
                />
              )}
            </div>
          ))}
        </Document>
      </div>
      {currentStep === 4 && activeBlockId && (
        <SignatureModal
          blockId={activeBlockId}
          onClose={() => setActiveBlockId(null)}
        />
      )}
    </div>
  );
}
